import { Ray } from 'node-ray/web';
import { format } from '@permafrost-dev/pretty-format';

const DataTrackingMixin = {
    data() {
        return {
            trackingRays: {},
            trackingStops: {},
        };
    },
};

const PackageInfo = {
    NAME: 'vue-ray',
    VERSION: '1.17.3',
};

const createPackageMetaProperty = obj => {
    obj.$rayMeta = PackageInfo;
};
const matchPattern = (str, patterns) => {
    for (let pattern of patterns) {
        pattern = pattern.replace(/\*/g, '.*');
        const re = new RegExp(pattern, 'g');
        if (re.exec(str)) {
            return true;
        }
    }
    return false;
};
const filterObjectByKeys = (obj, includeKeyPatterns) => {
    const result = {};
    Object.keys(obj).forEach(key => {
        if (matchPattern(key, includeKeyPatterns)) {
            result[key] = obj[key];
        }
    });
    return result;
};
const determineComponentNameDuringEvent = (options) => {
    var _a, _b, _c;
    const result = (_a = options === null || options === void 0 ? void 0 : options.name) !== null && _a !== void 0 ? _a : 'unknown';
    let filename = (_b = options === null || options === void 0 ? void 0 : options.__file) !== null && _b !== void 0 ? _b : 'src/unknown.js';
    if (result.length && result !== 'unknown') {
        return result;
    }
    filename = (_c = filename.split('/').pop()) !== null && _c !== void 0 ? _c : 'unknown.js';
    return filename.replace(/\.[\w-_]+$/, '');
};

class VueRay extends Ray {
    constructor() {
        super(...arguments);
        this.component = { $data: {}, $refs: {} };
        this.watch = () => { };
    }
    data() {
        if (this.component) {
            let data = Object.assign({}, this.component.$data);
            delete data['trackingRays'];
            delete data['trackingStops'];
            data = Object.freeze(data);
            this.table([data], 'Data');
        }
    }
    props() {
        if (this.component) {
            this.table([this.component.$props]);
        }
    }
    ref(name) {
        if (this.component) {
            this.html(this.component.$refs[name].innerHTML);
        }
    }
    track(name) {
        if (this.component && typeof this.component.trackingRays[name] === 'undefined') {
            this.component.trackingRays[name] = this.component.$ray(name);
            const onTrackedUpdate = (value, previousValue) => {
                this.component.trackingRays[name] = this.component.trackingRays[name].send({
                    info: {
                        variable: name,
                        tracking: true,
                    },
                    trackingData: {
                        value: value,
                        previous: previousValue,
                    },
                });
            };
            this.component.trackingStops[name] = this.watch(() => this.component[name], onTrackedUpdate, { deep: true });
        }
    }
    untrack(name) {
        if (this.component && typeof this.component.trackingStops[name] !== 'undefined') {
            this.component.trackingRays[name] = this.component.trackingRays[name].send({
                info: {
                    variable: name,
                    tracking: false,
                },
                trackedData: {
                    value: this.component[name],
                },
            });
            this.component.trackingStops[name]();
            delete this.component.trackingStops[name];
            delete this.component.trackingRays[name];
        }
    }
    static shouldDisplayEvent(name) {
        return VueRay.show_component_lifecycles.includes(name);
    }
    static showComponentLifecycles(names) {
        if (Array.isArray(VueRay.show_component_lifecycles)) {
            VueRay.show_component_lifecycles.push(...names);
        }
    }
    static stopShowingComponentLifecycles(names = []) {
        if (!names.length) {
            VueRay.show_component_lifecycles = [];
            return;
        }
        VueRay.show_component_lifecycles = VueRay.show_component_lifecycles.filter(name => !names.includes(name));
    }
    static useDefaultSettings(settings) {
        return super.useDefaultSettings(settings);
    }
    send(...args) {
        return super.send(...args);
    }
}
VueRay.show_component_lifecycles = [];
const ray = (...args) => {
    if (!args.length) {
        return VueRay.create();
    }
    return VueRay.create().send(...args);
};

class ErrorHandler {
    constructor(callback = null, window = null, rayInstance = null) {
        this.additionalInfoCallback = null;
        this.onVueError = (err, vm) => {
            const additionalInfoHtml = this.additionalInfoHtml(err, vm, 'vue');
            const stack = err.stack
                .replace(/^(\w+):(.+)$/m, '<div class="flex bg-white border-0">' +
                '<div class="flex-row bg-white text-red-400 pr-1 pl-1 border border-1 border-red-400">$1</div>' +
                '<div style="padding-top: 0.25em;" class="flex-row">$2</div>' +
                '</div>')
                .replace(/\n/, 'Stack Trace:\n')
                .replace(/ at ([^ ]+) (.*)$/gm, '<div class="inline"> at <span class="text-blue-700">$1</span> $2</div>');
            vm.$ray().sendCustom(`<pre>${stack}${additionalInfoHtml}</pre>`, 'Error').small().red();
        };
        this.onWindowError = (message, source, lineno, colno, error) => {
            var _a;
            const additionalInfoHtml = this.additionalInfoHtml(error, { message, source, lineno, colno }, 'error');
            const html = `
        <div class="flex bg-white border-0">
            <div class="flex-row bg-white text-red-400 pr-1 pl-1 border border-1 border-red-400">${message}</div>
            <div style="padding-top: 0.25em;" class="flex-row"><code>${source}</code></div>
        </div>${additionalInfoHtml}`;
            (_a = this.ray) === null || _a === void 0 ? void 0 : _a.html(`<pre>${html}</pre>`, 'Error').small().red();
        };
        this.onWindowUnhandledRejectionEvent = (event) => {
            var _a;
            const additionalInfoHtml = this.additionalInfoHtml(null, event, 'unhandled_rejection');
            const html = `
        <div class="flex bg-white border-0">
            <div class="flex-row bg-white text-red-400 pr-1 pl-1 border border-1 border-red-400">${event.reason}</div>
            <div style="padding-top: 0.25em;" class="flex-row"><code>${JSON.stringify(event)}</code></div>
        </div>${additionalInfoHtml}`;
            (_a = this.ray) === null || _a === void 0 ? void 0 : _a.html(`<pre>${html}</pre>`, 'Error').small().red();
        };
        this.additionalInfoCallback = callback;
        this.ray = rayInstance !== null && rayInstance !== void 0 ? rayInstance : ray();
        this.window = window !== null && window !== void 0 ? window : globalThis;
    }
    additionalInfo(err, ctx, type) {
        return this.additionalInfoCallback ? this.additionalInfoCallback(err, ctx, type) : '';
    }
    additionalInfoHtml(err, ctx, type) {
        let info = this.additionalInfo(err, ctx, type);
        if (typeof info === 'string' && !info.length) {
            return '';
        }
        if (typeof info !== 'string' && typeof info !== 'number') {
            info = format(info, { indent: 4, highlight: true });
            return (`<details open><summary class="text-black w-full block mt-1 cursor-pointer">Additional Info:</summary>` +
                `<div class="text-blue-600 w-full block pl-8">${info}</div></details>`);
        }
        return (`<div class="text-black w-full block mt-1">Additional Info:</div>` +
            `<div class="text-blue-600 w-full block pl-8">${info}</div>`);
    }
    installVueErrorHandler(vueConfig) {
        vueConfig.errorHandler = this.onVueError;
        return vueConfig;
    }
    installWindowErrorHandlers(onError = null, onUnhandled = null) {
        if (this.window) {
            this.window.onerror = onError !== null && onError !== void 0 ? onError : this.onWindowError;
            this.window.unhandledrejection = onUnhandled !== null && onUnhandled !== void 0 ? onUnhandled : this.onWindowUnhandledRejectionEvent;
        }
    }
}
const errorHandler = new ErrorHandler();

const initializeOptions = (options, vueConfig) => {
    var _a;
    if (typeof options.interceptErrors === 'function') {
        errorHandler.additionalInfoCallback = options.interceptErrors;
    }
    if (options.interceptErrors === true || typeof options.interceptErrors === 'function') {
        errorHandler.installVueErrorHandler(vueConfig);
        errorHandler.installWindowErrorHandlers();
    }
    let host = 'localhost', port = 23517, scheme = 'http', enabled_callback = null, sending_payload_callback = null, sent_payload_callback = null, nodeRaySettings = (_a = options['nodeRaySettings']) !== null && _a !== void 0 ? _a : {};
    options = Object.assign({ host, port, scheme, enabled_callback, sending_payload_callback, sent_payload_callback }, options);
    if (typeof options['host'] === 'string') {
        host = options.host;
    }
    if (typeof options['port'] === 'number') {
        port = options.port;
    }
    if (typeof options['scheme'] === 'string') {
        scheme = options.scheme;
    }
    if (typeof options['enabled_callback'] === 'function') {
        enabled_callback = options.enabled_callback;
    }
    if (typeof options['sending_payload_callback'] === 'function') {
        sending_payload_callback = options.sending_payload_callback;
    }
    if (typeof options['sent_payload_callback'] === 'function') {
        sent_payload_callback = options.sent_payload_callback;
    }
    if (typeof options['showComponentEvents'] !== 'undefined') {
        if (Array.isArray(typeof options.showComponentEvents)) {
            VueRay.show_component_lifecycles = options.showComponentEvents;
        }
        VueRay.show_component_lifecycles = options.showComponentEvents;
    }
    if (typeof nodeRaySettings['interceptConsoleLog'] !== 'undefined') {
        try {
            if (nodeRaySettings['interceptConsoleLog'] == true) {
                ray().interceptor().enable();
            }
            else {
                ray().interceptor().disable();
            }
        }
        catch (err) {
            console.error('failed to enable console.log interception:', err);
        }
    }
    VueRay.useDefaultSettings({
        host,
        port,
        scheme,
        enabled_callback,
        sending_payload_callback,
        sent_payload_callback,
        ...nodeRaySettings,
    });
};

let vue3Watch = null;
const conditionallyDisplayEvent = (eventName, options, rayInstance = null) => {
    var _a, _b;
    if (VueRay.shouldDisplayEvent(eventName)) {
        rayInstance = rayInstance !== null && rayInstance !== void 0 ? rayInstance : Vue3RayMixin.methods.$ray;
        if (!((_a = options === null || options === void 0 ? void 0 : options.__file) !== null && _a !== void 0 ? _a : false)) {
            return;
        }
        const componentName = determineComponentNameDuringEvent(options);
        rayInstance().table([
            `component ${eventName}: <code>${componentName}</code>`,
            `filename: <code>&lt;project root&gt;/${(_b = options === null || options === void 0 ? void 0 : options.__file) !== null && _b !== void 0 ? _b : 'unknown.js'}</code>`,
        ]);
    }
};
const Vue3RayMixin = {
    beforeCreate() {
        if (typeof vue3Watch === 'undefined' || vue3Watch === null) {
            vue3Watch = require('vue').watch;
        }
        conditionallyDisplayEvent('before-create', this.$options);
    },
    beforeMount() {
        conditionallyDisplayEvent('before-mount', this.$options);
    },
    created() {
        conditionallyDisplayEvent('created', this.$options);
    },
    mounted() {
        conditionallyDisplayEvent('mounted', this.$options);
    },
    unmounted() {
        conditionallyDisplayEvent('unmounted', this.$options);
    },
    updated() {
        conditionallyDisplayEvent('updated', this.$options);
    },
    methods: {
        $ray(...args) {
            const ray = VueRay.create();
            ray.component = this;
            ray.watch = vue3Watch;
            return ray.send(...args);
        },
    },
};

let vuexStateRay = null;
class VuexState {
    constructor(state, options = {}) {
        var _a, _b;
        this.state = filterObjectByKeys(Object.assign({}, state), (_b = (_a = options.trackingOptions) === null || _a === void 0 ? void 0 : _a.propNames) !== null && _b !== void 0 ? _b : ['one']);
    }
}
const isRayVuexPluginNamedColor = (color) => {
    return typeof color !== 'undefined' && color !== 'none';
};
const DefaultVuexPluginOptions = {
    trackState: false,
    logMutations: false,
    logActions: false,
    loggedMutationColor: 'none',
    loggedActionColor: 'none',
    trackingOptions: {
        moduleNames: ['*'],
        propNames: ['*'],
    },
};
const decorateEventType = (name, color) => {
    return `<span class="pr-1 text-${color}-600">${name}</span>`;
};
const sendEventInfoTable = (name, color, data, rayInstance = null) => {
    if (rayInstance === null) {
        rayInstance = () => ray();
    }
    return rayInstance().table({
        'Vuex Event': decorateEventType(name, color) + ' ' + data.type,
        Payload: data.payload,
    }, 'Vuex');
};
const VuexPlugin = (options = DefaultVuexPluginOptions, rayInstance = null) => {
    rayInstance = rayInstance !== null && rayInstance !== void 0 ? rayInstance : (() => ray());
    options = Object.assign({}, DefaultVuexPluginOptions, options);
    return (store) => {
        if (options.trackState) {
            store.subscribe((mutation, state) => {
                const tempState = Object.assign({}, state);
                const copiedState = Object.freeze(tempState);
                if (vuexStateRay === null) {
                    vuexStateRay = rayInstance();
                }
                vuexStateRay = vuexStateRay.send(new VuexState(copiedState, options));
            });
        }
        if (options.logMutations) {
            store.subscribe((mutation, state) => {
                const r = sendEventInfoTable('mutation', 'green', mutation, rayInstance);
                if (isRayVuexPluginNamedColor(options.loggedMutationColor)) {
                    r.color(options.loggedMutationColor);
                }
                r.hide();
            });
        }
        if (options.logActions) {
            store.subscribeAction((action, state) => {
                const r = sendEventInfoTable('action', 'blue', action, rayInstance);
                if (isRayVuexPluginNamedColor(options.loggedActionColor)) {
                    r.color(options.loggedActionColor);
                }
                r.hide();
            });
        }
    };
};

const RayVuexPlugin = VuexPlugin;
const RayPlugin = {
    install: (app, options) => {
        createPackageMetaProperty(app.config.globalProperties);
        initializeOptions(options, app.config);
        app.provide('ray', options);
        app.mixin(DataTrackingMixin);
        app.mixin(Vue3RayMixin);
    },
};

export default RayPlugin;
export { RayPlugin, RayVuexPlugin };
